import { collection, addDoc } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import headerImage from "../assets/img1.png";
import { db } from "../firebase";
import "../styles/ReviewForm.css";

const ratingQuestions = [
  { key: "staff", label: "How were the staff?" },
  { key: "facilities", label: "How were the facilities?" },
  { key: "cleanliness", label: "How clean was it?" },
  { key: "food", label: "How was the food?" },
  { key: "value", label: "Was it good value for money?" },
];

const emojiOptions = [
  { value: 1, symbol: "☹", label: "Poor" },
  { value: 2, symbol: "😐", label: "Okay" },
  { value: 3, symbol: "🙂", label: "Good" },
  { value: 4, symbol: "😁", label: "Excellent" },
];

const getRatingLabel = (value) => {
  const matchedOption = emojiOptions.find((option) => option.value === value);
  return matchedOption ? `${value} - ${matchedOption.label}` : "Not rated";
};

const ReviewForm = () => {
  const [ratings, setRatings] = useState({
    staff: 0,
    facilities: 0,
    cleanliness: 0,
    food: 0,
    value: 0,
  });
  const [review, setReview] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ipAddress, setIpAddress] = useState("Unknown");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitButtonRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const hotelName = queryParams.get("hotel") || "Centauria Lake Resort";

  const SERVICE_ID = "service_67xh1uo";
  const TEMPLATE_ID = "template_ufh5qxj";
  const PUBLIC_KEY = "hsCjAmsMh29aMNasg";

  const handleRating = (type, value) => {
    setRatings((prev) => ({ ...prev, [type]: value }));
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isFormValid =
    Object.values(ratings).every((val) => val > 0) &&
    (!email || isValidEmail(email));

  const handleSubmit = async () => {
    if (isSubmitting || !isFormValid) {
      return;
    }

    setIsSubmitting(true);

    const ratingValues = Object.values(ratings);
    const overallScore = (
      ratingValues.reduce((sum, value) => sum + value, 0) / ratingValues.length
    ).toFixed(1);

    const templateParams = {
      name: name || "None",
      email: email || "None",
      hotel_name: hotelName,
      overall: overallScore,
      overall_label: `${overallScore}/4`,
      staff: ratings.staff,
      staff_label: getRatingLabel(ratings.staff),
      facilities: ratings.facilities,
      facilities_label: getRatingLabel(ratings.facilities),
      cleanliness: ratings.cleanliness,
      cleanliness_label: getRatingLabel(ratings.cleanliness),
      food: ratings.food,
      food_label: getRatingLabel(ratings.food),
      value: ratings.value,
      value_label: getRatingLabel(ratings.value),
      meals: "N/A",
      food: "N/A",
      service: getRatingLabel(ratings.staff),
      ambiance: "N/A",
      review: review || "None",
      ip_address: ipAddress,
      submitted_at: new Date().toLocaleString(),
    };

    try {
      await addDoc(collection(db, "hotelReviews"), {
        ...ratings,
        review,
        name,
        email,
        createdAt: new Date(),
        hotelName,
        ipAddress,
      });

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });

      navigate("/thank-you");

      setRatings({
        staff: 0,
        facilities: 0,
        cleanliness: 0,
        food: 0,
        value: 0,
      });
      setReview("");
      setName("");
      setEmail("");
    } catch (error) {
      console.error("Error submitting review or sending email:", error);
      alert("Submission failed or email not sent. Please try again.");
      setIsSubmitting(false);
    }
  };

  const renderEmojiSelector = (type) => (
    <div className="emoji-scale" role="radiogroup" aria-label={type}>
      {emojiOptions.map((option) => {
        const isSelected = ratings[type] === option.value;

        return (
          <button
            key={option.value}
            type="button"
            className={isSelected ? "emoji-option selected" : "emoji-option"}
            onClick={() => handleRating(type, option.value)}
            aria-pressed={isSelected}
            aria-label={option.label}
          >
            <span className="emoji-symbol" aria-hidden="true">
              {option.symbol}
            </span>
          </button>
        );
      })}
    </div>
  );

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch((err) => console.error("IP fetch failed", err));
  }, []);

  useEffect(() => {
    if (isFormValid && submitButtonRef.current) {
      submitButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      submitButtonRef.current.focus();
    }
  }, [isFormValid]);

  return (
    <div className="review-wrapper">
      <div className="review-overlay"></div>

      <div className="review-container">
        <div className="review-header">
          <img className="review-hero-image" src={headerImage} alt={hotelName} />
          <div className="review-hero-overlay"></div>
          <div className="review-hero-content">
            <p className="review-eyebrow">Review</p>
            <h3 className="review-title">{hotelName}</h3>
            <p className="review-subtitle">Share your stay experience with us</p>
          </div>
        </div>

        <div className="meal-options">
          <h4>Tell us what we can improve</h4>
          <hr />

          <div className="ratings-grid">
            {ratingQuestions.map(({ key, label }) => (
              <div key={key} className="rating-row">
                <label>{label}</label>
                <div>{renderEmojiSelector(key)}</div>
              </div>
            ))}
          </div>

          <label className="field-label" htmlFor="review-message">
            Suggestions (optional)
          </label>
          <textarea
            id="review-message"
            placeholder="Share your experience..."
            value={review}
            onChange={(e) => setReview(e.target.value)}
          />
          <label className="field-label" htmlFor="review-name">
            Name (optional)
          </label>
          <input
            id="review-name"
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="field-label" htmlFor="review-email">
            Email (optional)
          </label>
          <input
            id="review-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {email && !isValidEmail(email) && (
            <p className="validation-error">
              Please enter a valid email address.
            </p>
          )}
        </div>

        <div className="review-footer">
          <div className="review-score-chip">
            <span>Ready to submit</span>
            <strong>
              {Object.values(ratings).filter((value) => value > 0).length}/5
            </strong>
          </div>

          {isFormValid && (
            <button
              ref={submitButtonRef}
              className="submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : " Submit Feedback"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewForm;
